import { ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';

import { DropdownEmptyState } from '@/components/DropdownEmptyState';
import { DropdownSearch } from '@/components/DropdownSearch';
import { FieldClearButton } from '@/components/FieldClearButton';
import { InputLabel } from '@/components/InputLabel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FIELD_PLACEHOLDER_TEXT_CLASS_NAMES,
  type FieldSize,
  inputVariants,
} from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { dropdownItemVariants } from '@/components/ui/select';

import { cn } from '@/lib/utils';

import { useCollapsedChips } from '@/hooks';

const CHIPS_PADDING_BY_SIZE: Record<FieldSize, string> = {
  sm: 'py-px',
  md: 'py-0.5',
  lg: 'py-1',
};

export type MultiSelectOption = {
  id: string | number;
  name: string;
};

export type MultiSelectProps = {
  options: MultiSelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  onClear?: () => void;
  label?: ReactNode;
  hint?: ReactNode;
  isRequired?: boolean;
  icon?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
  id?: string;
  size?: FieldSize;
  shouldCollapseChips?: boolean;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
};

export const MultiSelect = (props: MultiSelectProps) => {
  const {
    options,
    onValueChange,
    onClear,
    label,
    hint,
    isRequired,
    icon,
    placeholder,
    className,
    id,
    'aria-invalid': ariaInvalid,
    'aria-label': ariaLabel,
  } = props;
  const value = props.value ?? [];
  const isDisabled = props.disabled ?? false;
  const isSearchable = props.searchable ?? false;
  const size = props.size ?? 'md';
  const shouldCollapseChips = props.shouldCollapseChips ?? true;

  const { t } = useTranslation();
  const generatedId = useId();
  const selectId = id ?? generatedId;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOptions = options.filter((option) =>
    value.includes(String(option.id)),
  );
  const hasSelection = selectedOptions.length > 0;

  const { chipsRef, measureRef, visibleCount, minWidths } = useCollapsedChips(
    selectedOptions.map((option) => String(option.id)),
    shouldCollapseChips,
  );
  const visibleOptions = selectedOptions.slice(0, visibleCount);
  const hiddenCount = selectedOptions.length - visibleOptions.length;

  const normalizedSearch = search.toLowerCase();
  const filteredOptions =
    isSearchable && normalizedSearch
      ? options.filter((option) =>
          option.name.toLowerCase().includes(normalizedSearch),
        )
      : options;

  const onOptionToggle = (optionId: string | number) => {
    const optionIdString = String(optionId);
    onValueChange(
      value.includes(optionIdString)
        ? value.filter((selectedId) => selectedId !== optionIdString)
        : [...value, optionIdString],
    );
  };

  const onOptionRemove = (optionId: string | number) => {
    const optionIdString = String(optionId);
    onValueChange(value.filter((selectedId) => selectedId !== optionIdString));
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <InputLabel
          htmlFor={selectId}
          label={label}
          hint={hint}
          isRequired={isRequired}
          isInvalid={ariaInvalid}
        />
      )}
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <PopoverTrigger asChild>
          <Button
            id={selectId}
            type="button"
            variant="field"
            role="combobox"
            aria-expanded={isOpen}
            aria-invalid={ariaInvalid}
            aria-label={ariaLabel}
            disabled={isDisabled}
            className={cn(
              'group/trigger h-auto justify-between',
              inputVariants({ kind: 'trigger', size, height: 'min' }),
              'py-0',
              hasSelection && CHIPS_PADDING_BY_SIZE[size],
              hasSelection && !icon && 'pl-[7px]',
            )}
          >
            {icon && (
              <span className="text-muted-foreground flex shrink-0 items-center [&>svg]:size-5">
                {icon}
              </span>
            )}
            {hasSelection ? (
              <div
                ref={chipsRef}
                className={cn(
                  'relative flex min-w-0 flex-1 gap-2',
                  shouldCollapseChips
                    ? 'flex-nowrap overflow-hidden'
                    : 'flex-wrap',
                )}
              >
                {shouldCollapseChips && (
                  <div
                    ref={measureRef}
                    aria-hidden
                    className="pointer-events-none invisible absolute top-0 left-0 flex w-max gap-2"
                  >
                    {selectedOptions.map((option) => (
                      <Badge
                        variant="chip"
                        size="chip"
                        key={String(option.id)}
                        className="shrink-0"
                      >
                        <span>{option.name}</span>
                        <X className="size-3 shrink-0" />
                      </Badge>
                    ))}
                    <Badge
                      variant="chip"
                      size="chip"
                      className="shrink-0 px-3"
                    >
                      +{selectedOptions.length}
                    </Badge>
                  </div>
                )}
                {visibleOptions.map((option, index) => (
                  <Badge
                    variant="chip"
                    size="chip"
                    key={String(option.id)}
                    className="max-w-full min-w-0 shrink"
                    style={
                      shouldCollapseChips
                        ? { minWidth: minWidths[index] }
                        : undefined
                    }
                  >
                    <span className="truncate">{option.name}</span>
                    <FieldClearButton
                      className="-m-1 p-1 text-inherit [&>svg]:size-3"
                      onClear={() => onOptionRemove(option.id)}
                    />
                  </Badge>
                ))}
                {hiddenCount > 0 && (
                  <Badge
                    variant="chip"
                    size="chip"
                    className="shrink-0 px-3"
                  >
                    +{hiddenCount}
                  </Badge>
                )}
              </div>
            ) : (
              <span
                className={cn(
                  'text-muted-foreground flex-1 truncate text-left',
                  FIELD_PLACEHOLDER_TEXT_CLASS_NAMES[size],
                )}
              >
                {placeholder || ' '}
              </span>
            )}
            {onClear && hasSelection && !isDisabled && (
              <FieldClearButton onClear={onClear} />
            )}
            <ChevronDown className="ml-2 size-4 shrink-0 opacity-50 transition-transform duration-200 group-data-[state=open]/trigger:rotate-180" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          variant="dropdown"
          className="w-(--radix-popover-trigger-width)"
          align="start"
          onCloseAutoFocus={() => setSearch('')}
        >
          {isSearchable && (
            <DropdownSearch
              value={search}
              placeholder={
                typeof label === 'string' ? `${label}...` : t('Search')
              }
              className="mb-1"
              onChange={setSearch}
            />
          )}
          <div className="flex max-h-75 flex-col gap-1 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <DropdownEmptyState isSearchResult={options.length > 0} />
            ) : (
              filteredOptions.map((option) => {
                const optionIdString = String(option.id);
                const isSelected = value.includes(optionIdString);
                return (
                  <div
                    key={optionIdString}
                    role="option"
                    aria-selected={isSelected}
                    className={cn(
                      dropdownItemVariants({ size }),
                      'flex shrink-0 cursor-default items-center select-none',
                    )}
                    onClick={() => onOptionToggle(option.id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      tabIndex={-1}
                      aria-hidden
                      className="pointer-events-none"
                    />
                    <span className="flex-1">{option.name}</span>
                  </div>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
